import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import {
  ShoppingCart,
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardList,
  Menu,
  X,
  LogOut,
  Wifi,
  WifiOff,
  RefreshCw,
  Check,
  Lock,
  Tag,
  Truck,
  CreditCard,
  Banknote
} from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PuntoDeVenta() {
  const { user, logout } = useAuth();
  const { isOnline, pendingSync, syncData } = useOffline();
  const navigate = useNavigate();
  
  const [puntoVenta, setPuntoVenta] = useState(null);
  const [inventario, setInventario] = useState({});
  const [precios, setPrecios] = useState({});
  const [resumen, setResumen] = useState({ ventas: 0, entradas: 0, salidas: 0 });
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [diaCerrado, setDiaCerrado] = useState(false);
  const [sinListaPrecios, setSinListaPrecios] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!user?.punto_venta_id) {
      navigate('/mi-jornada');
      return;
    }
    
    try {
      // Check if day is closed
      const { data: diaCerradoData } = await axios.get(`${API}/dia-cerrado/${user.punto_venta_id}`);
      setDiaCerrado(diaCerradoData.cerrado);
      
      // Get punto de venta info
      const { data: puntos } = await axios.get(`${API}/puntos-venta`);
      const punto = puntos.find(p => p.id === user.punto_venta_id);
      setPuntoVenta(punto);
      
      // Get current inventory
      const { data: invData } = await axios.get(`${API}/inventario/actual/${user.punto_venta_id}`);
      setInventario(invData.productos || {});
      
      if (!invData.has_inicial) {
        navigate('/inventario-inicial');
        return;
      }
      
      // Get prices
      const { data: preciosData } = await axios.get(`${API}/precios/${user.punto_venta_id}`);
      const preciosObj = preciosData.precios || {};
      setPrecios(preciosObj);
      setSinListaPrecios(Object.values(preciosObj).every(p => !p || p === 0));
      
      // Get summary
      const { data: resumenData } = await axios.get(`${API}/resumen/${user.punto_venta_id}`);
      const totalVentas = Object.values(resumenData.ventas || {}).reduce((a, b) => a + b, 0);
      const totalEntradas = Object.values(resumenData.entradas || {}).reduce((a, b) => a + b, 0);
      const totalSalidas = Object.values(resumenData.salidas || {}).reduce((a, b) => a + b, 0);
      setResumen({ ventas: totalVentas, entradas: totalEntradas, salidas: totalSalidas });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (syncing || !isOnline) return;
    setSyncing(true);
    try {
      await syncData();
      await loadData();
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getTotalInventario = () => {
    return Object.values(inventario).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="pos-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-500">Punto de venta</p>
            <h1 className="text-lg font-bold text-zinc-900">{puntoVenta?.name}</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Day closed indicator */}
            {diaCerrado && (
              <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                <Lock className="w-3 h-3" />
                Día cerrado
              </div>
            )}
            
            {/* Connection status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isOnline ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
            }`}>
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {isOnline ? 'Online' : 'Offline'}
            </div>
            
            {/* Sync button */}
            {pendingSync > 0 && (
              <button
                onClick={handleSync}
                disabled={!isOnline || syncing}
                className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                data-testid="btn-sync"
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                {pendingSync}
              </button>
            )}
            
            {/* Menu button */}
            <button
              onClick={() => setShowMenu(true)}
              className="w-10 h-10 flex items-center justify-center"
              data-testid="btn-menu"
            >
              <Menu className="w-6 h-6 text-zinc-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Day Closed Banner */}
      {diaCerrado && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-bold text-red-900">Día cerrado</p>
              <p className="text-sm text-red-700">Ya se registró el corte de caja. No se pueden hacer más operaciones hoy.</p>
            </div>
          </div>
        </div>
      )}

      {/* No price list banner */}
      {sinListaPrecios && !diaCerrado && (
        <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Tag className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-amber-900">Sin lista de precios</p>
              <p className="text-sm text-amber-700">Contacta al administrador para asignar una lista de precios.</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 text-center border border-zinc-200">
            <p className="text-3xl font-bold text-zinc-900">{getTotalInventario()}</p>
            <p className="text-xs text-zinc-500 mt-1">Inventario</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center border border-zinc-200">
            <p className="text-3xl font-bold text-green-600">{resumen.ventas}</p>
            <p className="text-xs text-zinc-500 mt-1">Vendidas</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center border border-zinc-200">
            <p className="text-3xl font-bold text-blue-600">{resumen.entradas}</p>
            <p className="text-xs text-zinc-500 mt-1">Entradas</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex-1 px-4 pb-4">
        <h2 className="text-sm font-medium text-zinc-500 mb-3">ACCIONES RÁPIDAS</h2>
        
        <div className="space-y-3">
          {/* Venta */}
          <button
            onClick={() => !diaCerrado && !sinListaPrecios && navigate('/registrar-venta')}
            disabled={diaCerrado || sinListaPrecios}
            className={`w-full rounded-xl p-5 flex items-center gap-4 ${
              diaCerrado || sinListaPrecios
                ? 'bg-zinc-200 opacity-60 cursor-not-allowed'
                : 'bg-green-600 text-white active:bg-green-700'
            }`}
            data-testid="btn-venta"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              diaCerrado || sinListaPrecios ? 'bg-zinc-300' : 'bg-white/20'
            }`}>
              {diaCerrado ? <Lock className="w-6 h-6 text-zinc-500" /> : sinListaPrecios ? <Tag className="w-6 h-6 text-zinc-500" /> : <ShoppingCart className="w-6 h-6" />}
            </div>
            <div className="text-left">
              <p className={`text-xl font-bold ${diaCerrado || sinListaPrecios ? 'text-zinc-600' : ''}`}>Registrar Venta</p>
              <p className={`text-sm ${diaCerrado || sinListaPrecios ? 'text-zinc-500' : 'text-green-100'}`}>
                {diaCerrado ? 'Bloqueado - día cerrado' : sinListaPrecios ? 'Sin lista de precios asignada' : 'Vender piezas de pollo'}
              </p>
            </div>
          </button>

          {/* Entrada */}
          <button
            onClick={() => !diaCerrado && navigate('/movimiento', { state: { tipo: 'entrada' } })}
            disabled={diaCerrado}
            className={`w-full rounded-xl p-5 flex items-center gap-4 ${
              diaCerrado 
                ? 'bg-zinc-100 opacity-60 cursor-not-allowed border border-zinc-200' 
                : 'bg-white border border-zinc-200 active:bg-zinc-50'
            }`}
            data-testid="btn-entrada"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              diaCerrado ? 'bg-zinc-200' : 'bg-blue-100'
            }`}>
              {diaCerrado ? <Lock className="w-6 h-6 text-zinc-400" /> : <ArrowDownToLine className="w-6 h-6 text-blue-600" />}
            </div>
            <div className="text-left">
              <p className={`text-xl font-bold ${diaCerrado ? 'text-zinc-500' : 'text-zinc-900'}`}>Entrada Recibida</p>
              <p className="text-zinc-500 text-sm">
                {diaCerrado ? 'Bloqueado - día cerrado' : 'Recibir piezas del repartidor'}
              </p>
            </div>
          </button>

          {/* Salida */}
          <button
            onClick={() => !diaCerrado && navigate('/movimiento', { state: { tipo: 'salida' } })}
            disabled={diaCerrado}
            className={`w-full rounded-xl p-5 flex items-center gap-4 ${
              diaCerrado 
                ? 'bg-zinc-100 opacity-60 cursor-not-allowed border border-zinc-200' 
                : 'bg-white border border-zinc-200 active:bg-zinc-50'
            }`}
            data-testid="btn-salida"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              diaCerrado ? 'bg-zinc-200' : 'bg-orange-100'
            }`}>
              {diaCerrado ? <Lock className="w-6 h-6 text-zinc-400" /> : <ArrowUpFromLine className="w-6 h-6 text-orange-600" />}
            </div>
            <div className="text-left">
              <p className={`text-xl font-bold ${diaCerrado ? 'text-zinc-500' : 'text-zinc-900'}`}>Salida / Resurtido</p>
              <p className="text-zinc-500 text-sm">
                {diaCerrado ? 'Bloqueado - día cerrado' : 'Enviar piezas a otro punto'}
              </p>
            </div>
          </button>

          {/* Movimiento de Dinero */}
          <button
            onClick={() => navigate('/movimiento-dinero')}
            className="w-full rounded-xl p-5 flex items-center gap-4 bg-white border border-zinc-200 active:bg-zinc-50"
            data-testid="btn-movimiento-dinero"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-100">
              <Banknote className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-zinc-900">Movimiento de Dinero</p>
              <p className="text-zinc-500 text-sm">Enviar o confirmar recepción de efectivo</p>
            </div>
          </button>

          {/* Traslados */}
          <button
            onClick={() => navigate('/traslados-pos')}
            className="w-full rounded-xl p-5 flex items-center gap-4 bg-white border border-zinc-200 active:bg-zinc-50"
            data-testid="btn-traslados"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-100">
              <Truck className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-zinc-900">Traslados</p>
              <p className="text-zinc-500 text-sm">Confirmar salidas y entradas</p>
            </div>
          </button>

          {/* Abonos de clientes */}
          <button
            onClick={() => navigate('/abonos-clientes')}
            className="w-full rounded-xl p-5 flex items-center gap-4 bg-white border border-zinc-200 active:bg-zinc-50"
            data-testid="btn-abonos"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-zinc-900">Abonos de Clientes</p>
              <p className="text-zinc-500 text-sm">Registrar pago de venta a crédito</p>
            </div>
          </button>

          {/* Corte */}
          <button
            onClick={() => navigate('/corte')}
            className={`w-full rounded-xl p-5 flex items-center gap-4 ${
              diaCerrado 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-white border border-zinc-200 active:bg-zinc-50'
            }`}
            data-testid="btn-corte"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              diaCerrado ? 'bg-green-100' : 'bg-purple-100'
            }`}>
              {diaCerrado ? <Check className="w-6 h-6 text-green-600" /> : <ClipboardList className="w-6 h-6 text-purple-600" />}
            </div>
            <div className="text-left">
              <p className={`text-xl font-bold ${diaCerrado ? 'text-green-700' : 'text-zinc-900'}`}>
                {diaCerrado ? 'Ver Corte de Caja' : 'Corte de Caja'}
              </p>
              <p className={`text-sm ${diaCerrado ? 'text-green-600' : 'text-zinc-500'}`}>
                {diaCerrado ? 'Corte completado' : 'Cerrar el día'}
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Inventory Quick View */}
      <div className="px-4 pb-6">
        <button
          onClick={() => navigate('/ver-inventario')}
          className="w-full bg-zinc-100 rounded-xl p-4 text-left"
          data-testid="btn-ver-inventario"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-zinc-500">INVENTARIO ACTUAL</span>
            <span className="text-sm text-blue-600 font-medium">Ver todo →</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRODUCTOS.slice(0, 5).map((producto) => {
              const qty = inventario[producto.clave] || 0;
              return (
                <div key={producto.clave} className="bg-white px-3 py-2 rounded-lg text-sm">
                  <span className="text-zinc-500">{producto.nombre}:</span>
                  <span className={`ml-1 font-bold ${qty <= 5 ? 'text-red-600' : 'text-zinc-900'}`}>
                    {qty}
                  </span>
                </div>
              );
            })}
          </div>
        </button>
      </div>

      {/* Side Menu */}
      {showMenu && (
        <div className="fixed inset-0 z-50" data-testid="side-menu">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <p className="font-bold text-zinc-900">{user?.name}</p>
                <p className="text-sm text-zinc-500">{user?.email}</p>
              </div>
              <button onClick={() => setShowMenu(false)}>
                <X className="w-6 h-6 text-zinc-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-2">
              <button
                onClick={() => { setShowMenu(false); navigate('/configuracion'); }}
                className="w-full p-3 text-left rounded-lg hover:bg-zinc-100 font-medium"
              >
                ⚙️ Configuración
              </button>
              <div className="border-t border-zinc-100" />
              <button
                onClick={() => { setShowMenu(false); navigate('/precios'); }}
                className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
              >
                Ver precios del día
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate('/mi-nomina'); }}
                className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
              >
                Mi nómina
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate('/ver-inventario'); }}
                className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
              >
                Ver inventario completo
              </button>
              {(user?.role === 'admin' || user?.role === 'propietario') && (
                <>
                  <div className="border-t border-zinc-200 my-2" />
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/usuarios'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                  >
                    Administrar usuarios
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/precios'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                  >
                    Configurar precios
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/listas-precios'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                  >
                    Listas de precios
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/reportes'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                    data-testid="btn-menu-reportes"
                  >
                    Reportes Excel
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/asignaciones'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                    data-testid="btn-menu-asignaciones"
                  >
                    Asignar pollos
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/nomina'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                  >
                    Nómina
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); navigate('/admin/inventario'); }}
                    className="w-full p-3 text-left rounded-lg hover:bg-zinc-100"
                    data-testid="btn-menu-inventario-dashboard"
                  >
                    Tablero de inventario
                  </button>
                </>
              )}
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-200">
              <button
                onClick={logout}
                className="w-full p-3 text-left rounded-lg text-red-600 hover:bg-red-50 flex items-center gap-2"
                data-testid="btn-logout"
              >
                <LogOut className="w-5 h-5" />
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
